import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import AddToEHR from '../components/AddToEHR';
import { 
  MessageSquare, Send, Users, User, Circle, ArrowLeft,
  RefreshCw, Plus, Loader2, Search, Check, CheckCheck,
  Image, Paperclip, Smile, Phone, Video, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatCenter() {
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  
  const token = localStorage.getItem('hospice_token');
  const currentUserId = localStorage.getItem('user_id');

  const fetchRooms = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRooms(data.rooms || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  }, [token]);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/online-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setOnlineUsers(data.online_users || []);
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  }, [token]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUnreadCount(data.unread || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token]);

  const fetchMessages = useCallback(async (roomId) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms/${roomId}/messages?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMessages(data.messages || []);
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchRooms(), fetchOnlineUsers(), fetchUnreadCount()]);
    setLoading(false);
  }, [fetchRooms, fetchOnlineUsers, fetchUnreadCount]);

  useEffect(() => {
    fetchAll();
    
    // Connect to chat WebSocket
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchAll]);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.id);
    }
  }, [selectedRoom, fetchMessages]);

  const connectWebSocket = () => {
    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/chat/ws?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('Chat WebSocket connected');
        // Send auth
        ws.send(JSON.stringify({ type: 'auth', token }));
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      };
      
      ws.onclose = () => {
        console.log('Chat WebSocket disconnected');
        // Reconnect after 3 seconds
        setTimeout(connectWebSocket, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'new_message':
        if (selectedRoom && data.room_id === selectedRoom.id) {
          setMessages(prev => [...prev, data.message]);
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        fetchRooms();
        fetchUnreadCount();
        break;
      case 'typing':
        // Handle typing indicator
        break;
      case 'presence':
        fetchOnlineUsers();
        break;
      case 'read_receipt':
        // Update read status
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom) return;
    
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms/${selectedRoom.id}/message`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newMessage,
          message_type: 'text'
        })
      });
      
      const data = await res.json();
      if (data.success) {
        setNewMessage('');
        // Message will be added via WebSocket
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isUserOnline = (userId) => onlineUsers.includes(userId);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="chat-center">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900/50 to-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.location.href = '/dashboard'}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Messages</h1>
                <p className="text-slate-400 text-sm">
                  {onlineUsers.length} online • {unreadCount} unread
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AddToEHR 
                buttonVariant="default"
                buttonSize="default"
                buttonText="Add to EHR"
                className="bg-purple-600 hover:bg-purple-700"
              />
              <Button 
                onClick={() => setShowNewChatDialog(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" /> New Chat
              </Button>
              <Button 
                variant="outline"
                onClick={fetchAll}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-4 h-[calc(100vh-200px)]">
          {/* Rooms List */}
          <div className="col-span-4 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search conversations..." 
                  className="bg-slate-700 border-slate-600 pl-10 text-white"
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {rooms.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                  <p>No conversations yet</p>
                  <Button 
                    className="mt-4 bg-purple-600 hover:bg-purple-700"
                    onClick={() => setShowNewChatDialog(true)}
                  >
                    Start a Chat
                  </Button>
                </div>
              ) : (
                rooms.map((room) => (
                  <div
                    key={room.id}
                    onClick={() => setSelectedRoom(room)}
                    className={`p-4 border-b border-slate-700/50 cursor-pointer hover:bg-slate-700/50 transition-colors ${
                      selectedRoom?.id === room.id ? 'bg-slate-700/50 border-l-2 border-l-purple-500' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          {room.room_type === 'direct' ? (
                            <User className="w-5 h-5 text-white" />
                          ) : (
                            <Users className="w-5 h-5 text-white" />
                          )}
                        </div>
                        {room.online_participants?.length > 0 && (
                          <Circle className="absolute -bottom-0.5 -right-0.5 w-3 h-3 text-green-400 fill-green-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-white truncate">{room.name}</p>
                          {room.my_unread > 0 && (
                            <Badge className="bg-purple-500 text-white text-xs">
                              {room.my_unread}
                            </Badge>
                          )}
                        </div>
                        {room.last_message && (
                          <p className="text-sm text-slate-400 truncate">
                            {room.last_message.sender_name}: {room.last_message.content}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="col-span-8 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden flex flex-col">
            {selectedRoom ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      {selectedRoom.room_type === 'direct' ? (
                        <User className="w-5 h-5 text-white" />
                      ) : (
                        <Users className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{selectedRoom.name}</p>
                      <p className="text-xs text-slate-400">
                        {selectedRoom.participant_ids?.length || 0} participants
                        {selectedRoom.online_participants?.length > 0 && (
                          <span className="text-green-400"> • {selectedRoom.online_participants.length} online</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                      <Phone className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                      <Video className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => {
                    const isOwn = message.sender_id === currentUserId;
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''}`}>
                          {!isOwn && (
                            <p className="text-xs text-slate-400 mb-1">{message.sender_name}</p>
                          )}
                          <div className={`rounded-2xl px-4 py-2 ${
                            isOwn 
                              ? 'bg-purple-600 text-white rounded-br-none' 
                              : 'bg-slate-700 text-white rounded-bl-none'
                          }`}>
                            <p>{message.content}</p>
                          </div>
                          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                            <span className="text-xs text-slate-500">
                              {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isOwn && (
                              message.read_by?.length > 1 
                                ? <CheckCheck className="w-3 h-3 text-blue-400" />
                                : <Check className="w-3 h-3 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-slate-700">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                      <Paperclip className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                      <Image className="w-5 h-5" />
                    </Button>
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type a message..."
                      className="flex-1 bg-slate-700 border-slate-600 text-white"
                    />
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                      <Smile className="w-5 h-5" />
                    </Button>
                    <Button 
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-500" />
                  <p className="text-lg">Select a conversation</p>
                  <p className="text-sm">or start a new chat</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Chat Dialog */}
      <Dialog open={showNewChatDialog} onOpenChange={setShowNewChatDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Start New Chat</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Chat Name</label>
              <Input placeholder="Enter chat name..." className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Chat Type</label>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="border-slate-600">
                  <User className="w-4 h-4 mr-2" /> Direct Message
                </Button>
                <Button variant="outline" className="border-slate-600">
                  <Users className="w-4 h-4 mr-2" /> Group Chat
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowNewChatDialog(false)}>Cancel</Button>
              <Button className="bg-purple-600 hover:bg-purple-700">Create Chat</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
